import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy,
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  doc, 
  onSnapshot,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { chatModel } from '../data/chatModel';
import { notificationModel } from '../data/notificationModel';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { createNotification } = useNotification();
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState({}); // { chatId: [messages] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load user's chats
  useEffect(() => {
    if (!currentUser) {
      setChats([]);
      return;
    }

    const unsubscribe = loadUserChats();
    return () => unsubscribe();
  }, [currentUser]);

  // Load chats where user is a participant
  const loadUserChats = () => {
    if (!currentUser) return () => {};

    setLoading(true);
    setError(null);

    const chatsRef = collection(db, chatModel.collection);
    
    // Try with index first, fallback to simple query
    let q;
    try {
      q = query(
        chatsRef,
        where('participants', 'array-contains', currentUser.uid),
        orderBy('lastMessageAt', 'desc'),
        firestoreLimit(50)
      );
    } catch (error) {
      // Fallback: query without orderBy
      q = query(
        chatsRef,
        where('participants', 'array-contains', currentUser.uid),
        firestoreLimit(50)
      );
    }

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const chatsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Ensure id is always from doc.id, not from data (in case data has id field that overrides)
          const chatData = {
            ...data,
            id: doc.id // Always use doc.id as the source of truth
          };
          
          // Debug: Log if id is missing
          if (!chatData.id) {
            console.error('Chat without ID after mapping:', chatData, 'doc.id:', doc.id);
          }
          
          return chatData;
        });
        
        // Debug: Log first chat structure
        if (chatsData.length > 0) {
          console.log('ChatContext - First chat after mapping:', chatsData[0]);
          console.log('ChatContext - First chat has id?', chatsData[0].id);
          console.log('ChatContext - First chat keys:', Object.keys(chatsData[0]));
        }
        
        // Sort manually if needed
        chatsData.sort((a, b) => {
          const dateA = a.lastMessageAt?.toDate?.() || new Date(a.lastMessageAt || 0);
          const dateB = b.lastMessageAt?.toDate?.() || new Date(b.lastMessageAt || 0);
          return dateB - dateA;
        });
        
        setChats(chatsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading chats:', error);
        setError('เกิดข้อผิดพลาดในการโหลดแชท');
        setLoading(false);
      }
    );

    return unsubscribe;
  };

  // Load messages for a chat
  const loadMessages = async (chatId) => {
    if (!currentUser) return [];

    try {
      setLoading(true);
      setError(null);

      const messagesRef = collection(db, chatModel.messagesCollection);
      const q = query(
        messagesRef,
        where('chatId', '==', chatId),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setMessages(prev => ({
        ...prev,
        [chatId]: messagesData
      }));

      return messagesData;
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('เกิดข้อผิดพลาดในการโหลดข้อความ');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to messages for real-time updates
  const subscribeToMessages = (chatId) => {
    if (!currentUser) return () => {};

    const messagesRef = collection(db, chatModel.messagesCollection);
    const q = query(
      messagesRef,
      where('chatId', '==', chatId),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const messagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setMessages(prev => ({
          ...prev,
          [chatId]: messagesData
        }));

        // Mark messages as read
        messagesData.forEach(message => {
          if (message.senderId !== currentUser.uid && !message.read) {
            markMessageAsRead(message.id);
          }
        });
      },
      (error) => {
        console.error('Error subscribing to messages:', error);
      }
    );

    return unsubscribe;
  };

  // Find or create chat
  const findOrCreateChat = async (type, otherUserId, orderId = null, productId = null, storeId = null) => {
    if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบ');

    try {
      setLoading(true);
      setError(null);

      // Try to find existing chat
      const chatsRef = collection(db, chatModel.collection);
      let q;
      
      if (type === chatModel.types.ORDER && orderId) {
        q = query(
          chatsRef,
          where('type', '==', type),
          where('orderId', '==', orderId)
        );
      } else if (type === chatModel.types.PRODUCT_INQUIRY && productId) {
        q = query(
          chatsRef,
          where('type', '==', type),
          where('productId', '==', productId),
          where('participants', 'array-contains', currentUser.uid)
        );
      } else {
        q = query(
          chatsRef,
          where('type', '==', type),
          where('participants', 'array-contains', currentUser.uid)
        );
      }

      const snapshot = await getDocs(q);
      const existingChat = snapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(currentUser.uid) && 
               data.participants.includes(otherUserId);
      });

      if (existingChat) {
        return existingChat.id;
      }

      // Create new chat
      const participants = [currentUser.uid, otherUserId].sort();
      const newChat = chatModel.createChat(type, participants, orderId, productId, storeId);
      const chatRef = await addDoc(chatsRef, newChat);
      
      return chatRef.id;
    } catch (error) {
      console.error('Error finding/creating chat:', error);
      setError('เกิดข้อผิดพลาดในการสร้างแชท');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const sendMessage = async (chatId, text) => {
    if (!currentUser || !text.trim()) throw new Error('กรุณาเข้าสู่ระบบและกรอกข้อความ');

    try {
      setLoading(true);
      setError(null);

      // Create message
      const message = chatModel.createMessage(
        chatId,
        currentUser.uid,
        currentUser.displayName || currentUser.email,
        text.trim()
      );

      const messagesRef = collection(db, chatModel.messagesCollection);
      const messageRef = await addDoc(messagesRef, message);

      // Update chat
      const chatRef = doc(db, chatModel.collection, chatId);
      const chatSnap = await getDoc(chatRef);
      
      if (chatSnap.exists()) {
        const chatData = chatSnap.data();
        const otherParticipant = chatData.participants.find(id => id !== currentUser.uid);
        
        await updateDoc(chatRef, {
          lastMessage: text.trim(),
          lastMessageAt: new Date(),
          lastMessageBy: currentUser.uid,
          unreadCount: {
            ...chatData.unreadCount,
            [otherParticipant]: (chatData.unreadCount[otherParticipant] || 0) + 1
          },
          updatedAt: new Date().toISOString()
        });

        // Send notification
        try {
          await createNotification(
            otherParticipant,
            notificationModel.types.CHAT_NEW,
            'ข้อความใหม่',
            `${currentUser.displayName || currentUser.email}: ${text.trim().substring(0, 50)}${text.trim().length > 50 ? '...' : ''}`,
            { chatId }
          );
        } catch (notifError) {
          console.error('Error creating chat notification:', notifError);
        }
      }

      return messageRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      setError('เกิดข้อผิดพลาดในการส่งข้อความ');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Mark message as read
  const markMessageAsRead = async (messageId) => {
    if (!currentUser) return;

    try {
      const messageRef = doc(db, chatModel.messagesCollection, messageId);
      await updateDoc(messageRef, {
        read: true,
        readAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark all messages in chat as read
  const markChatAsRead = async (chatId) => {
    if (!currentUser) return;

    try {
      const chatRef = doc(db, chatModel.collection, chatId);
      const chatSnap = await getDoc(chatRef);
      
      if (chatSnap.exists()) {
        await updateDoc(chatRef, {
          unreadCount: {
            ...chatSnap.data().unreadCount,
            [currentUser.uid]: 0
          },
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error marking chat as read:', error);
    }
  };

  const value = {
    chats,
    messages,
    loading,
    error,
    findOrCreateChat,
    sendMessage,
    loadMessages,
    subscribeToMessages,
    markMessageAsRead,
    markChatAsRead
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};


